import i18n from 'helpers/i18n';
import messages from './messages';
import { useLayoutEffect } from 'react';
import { useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import LanguageSelector from 'components/LanguageSelector';
import teachstoneBg from 'images/teachstone-bg.jpg';
import useCurrentUser from 'hooks/useCurrentUser';
import useDocumentTitle from 'hooks/useDocumentTitle';
import { AUTH_PATHS_REGEX } from 'helpers/authPaths';
import { Wrapper, BackgroundImg } from './Styled';

function UsersPage() {
  useDocumentTitle(i18n.ft(messages.login));
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  // There is no user, and this isn't one of the public auth pages -- in practice
  // /users/onboarding, the only authenticated page in this route subtree.
  //
  // pages/App/App.js applies this same rule and is what normally redirects after
  // the session-expiry interceptor clears the store, but it cannot run here:
  // /users/* is a sibling route subtree, so App never renders for these paths.
  // Without this, an expiring session on onboarding left the user sitting on the
  // onboarding screen, logged out, with nothing to click.
  const hasNoSession =
    !currentUser.id && !AUTH_PATHS_REGEX.test(location.pathname);

  useLayoutEffect(() => {
    // Handled by the <Navigate> below rather than here. navigate() from a layout
    // effect is a no-op on first mount -- react-router marks its navigator active
    // in its own layout effect, and layout effects run child-before-parent -- and
    // first mount (landing on onboarding with a dead session) is exactly the case
    // this guards. A render-time redirect has no such ordering problem, and
    // mirrors how App.js does it.
    if (hasNoSession) {
      return;
    }

    if (currentUser.skipOnboarding) {
      return;
    }

    if (currentUser.id) {
      const isOnboardingCompleted = Boolean(currentUser.onboarding_viewed_at);

      if (isOnboardingCompleted) {
        navigate('/', { replace: true });
      } else {
        navigate('/users/onboarding', { replace: true });
      }
    }
  }, [
    navigate,
    hasNoSession,
    location.pathname,
    currentUser.id,
    currentUser.onboarding_viewed_at,
    currentUser.skipOnboarding
  ]);

  if (hasNoSession) {
    return (
      <Navigate
        replace
        to={`/users/login?redirect_to=${encodeURIComponent(location.pathname)}`}
      />
    );
  }

  return (
    <Wrapper>
      <BackgroundImg alt="" src={teachstoneBg} />
      <main>
        <LanguageSelector />
        <Outlet />
      </main>
    </Wrapper>
  );
}

export default UsersPage;
